import React, { useCallback, useMemo } from 'react';
import { gql } from '@apollo/client';
import { useBreakpoint } from '@moda/om';
import { compact, useFeatureFlag } from '@moda/portal-stanchions';
import { Swiper as SwiperCore } from 'swiper';
import { MobileProductMediaGalleryFragment } from '../../generated/types';
import { getActiveVariantBy } from '../../lib/getActiveVariantBy';
import { tc } from '../../lib/trackingContext';
import { useList } from '../../hooks/useList';
import { usePageType } from '../../hooks/usePageType';
import { SwiperCarousel } from '../Swiper';
import { EnableLazyLoad } from '../LazyLoad';
import { ResponsiveImage } from '../ResponsiveImage';
import { ProductDetailPageVideo, PRODUCT_VIDEOS_FRAGMENT } from '../ProductDetailPageVideo';

import './MobileProductMediaGallery.scss';

export const MOBILE_PRODUCT_MEDIA_GALLERY_VARIANT_FRAGMENT = gql`
  fragment MobileProductMediaGalleryVariantFragment on VariantData {
    id
    primaryImage: primary_image_urls {
      mediumLarge: medium_large
      medium
    }
    alternateImages: alternate_image_urls {
      mediumLarge: medium_large
      medium
    }
  }
`;

export const MOBILE_PRODUCT_MEDIA_GALLERY_FRAGMENT = gql`
  fragment MobileProductMediaGalleryFragment on Variant {
    id
    name
    masterVariant: master_variants_data {
      ...MobileProductMediaGalleryVariantFragment
    }
    otherVariants: other_variants_data {
      ...MobileProductMediaGalleryVariantFragment
    }
    ...ProductVideosFragment
  }
  ${MOBILE_PRODUCT_MEDIA_GALLERY_VARIANT_FRAGMENT}
  ${PRODUCT_VIDEOS_FRAGMENT}
`;

export interface Props {
  variant: MobileProductMediaGalleryFragment;
}

const SM_SIZES = {
  maxWidth: 700,
  maxHeight: 1200
};

const XS_SIZES = {
  maxWidth: 400,
  maxHeight: 685
};

export const MobileProductMediaGallery: React.FC<Props> = ({ variant }) => {
  const { matches: isExtraSmallScreen } = useBreakpoint({ lt: 'xs' });
  const optimizationEnabled = useFeatureFlag('optimize-pdp-images-mobile', [
    { default: false },
    { if: true, then: true }
  ]);

  const { maxWidth, maxHeight } = isExtraSmallScreen ? XS_SIZES : SM_SIZES;

  const activeVariant = useMemo(() => getActiveVariantBy(variant, 'id', variant.id), [variant]);

  const [srcs, { filter: filterSrcs }] = useList(
    useMemo(
      () =>
        compact(
          compact([activeVariant.primaryImage, ...activeVariant.alternateImages]).map(
            ({ mediumLarge, medium }) => (optimizationEnabled ? medium : mediumLarge)
          )
        ),
      [activeVariant.alternateImages, activeVariant.primaryImage, optimizationEnabled]
    )
  );

  const pagetype = usePageType();

  const trackSlideSwiped = useCallback(
    ({ realIndex }: SwiperCore) => {
      if (realIndex > srcs.length - 1) {
        tc.track('Video Swiped', {
          addToScopeOnce: { variant, activeVariant },
          addToTrackingEvent: { pagetype }
        });
      }

      if (realIndex <= srcs.length - 1) {
        tc.track('Alternate Image Swiped', {
          addToScopeOnce: { variant, activeVariant },
          addToTrackingEvent: { pagetype }
        });
      }
    },
    [srcs.length, variant, activeVariant, pagetype]
  );

  return (
    <div className="MobileProductMediaGallery">
      <SwiperCarousel
        className="MobileProductMediaGallery__carousel"
        classNameSlide="MobileProductMediaGallery__slide"
        identifier={`MobileProductMediaGallery-${variant.id}`}
        loop
        key={variant.id}
        onSlideChange={trackSlideSwiped}
      >
        {srcs.map((src, index) => {
          const image = (
            <ResponsiveImage
              key={src}
              className="MobileProductMediaGallery__image"
              src={src}
              alt={variant.name || 'Product view'}
              maxWidth={maxWidth}
              maxHeight={maxHeight}
              onError={() => filterSrcs(item => item !== src)}
            />
          );

          return index > 0 ? <EnableLazyLoad key={src}>{image}</EnableLazyLoad> : image;
        })}

        {variant.videos?.map(video => (
          <div className="MobileProductMediaGallery__video-wrapper" key={video.id}>
            <div className="MobileProductMediaGallery__video">
              <ProductDetailPageVideo videos={variant.videos} videoId={video.id} />
            </div>
          </div>
        ))}
      </SwiperCarousel>
    </div>
  );
};
